import { useEffect, useState, useMemo } from 'react';
import { lang } from 'zf-infra';
import { join } from 'light-component';
import { Select as ASelect, Tag } from 'antd';
import { useBreakpoint } from '../../../../hooks';
import { t } from '../../../i18n';
import styles from '../style.module.less';
import { useDeepCompareMemoize } from './useDeepCompareMemoize';
import type { ReactElement } from 'react';
import type { SelectOption, SelectProps, ExtendSelectOption } from '../types';

const SELECT_ALL = 'all';
/**
 * 默认select下拉加载50个，防止数据过多页面卡顿
*/
const pageSize = 50;

export function Select(props: SelectProps): ReactElement {
  const {
    disabled = false,
    value,
    loading = false,
    options,
    placeholder,
    onChange,
    labelRender,
    maxTagCount = 1,
    defaultValue,
    mode,
    allowClear = false,
    clearAll = false,
    selectAll = false,
    skipFilterInvalid = false,
    autoClearSearchValue = true
  } = props;

  let initValue: string[];
  if (Array.isArray(value)) {
    initValue = value;
  } else {
    initValue = (value === undefined) ? [] : [value];
  }

  const { md } = useBreakpoint();
  const [selected, setSelected] = useState<string[]>(initValue);
  const [scrollPage, setScrollPage] = useState(1);
  const [keyWords, setKeyWords] = useState('');
  const [optionData, setOptionData] = useState<SelectOption[]>([]);
  // 过滤掉options中不存在的selected
  const optionSelected = useMemo(
    () => (skipFilterInvalid ? initValue : lang.intersection(options?.map(option => option.value), initValue))
    , [options, value]
  );
  const optionsMemo = useDeepCompareMemoize(options);

  const onBlur = (): void => {
    setKeyWords('');
    setOptionData(options.slice(0, pageSize));
  };

  const onSearch = (val: string): void => {
    const matches = getMatches(val, options);
    setOptionData((matches.length > pageSize || value === '') ? matches.slice(0, pageSize) : matches);
    setKeyWords(val);
  };

  const onScroll = (ele: React.UIEvent<HTMLDivElement>): void => {
    if (ele.target instanceof HTMLDivElement) {
      const { target } = ele;
      const rmHeight = target.scrollHeight - target.scrollTop;
      const clHeight = target.clientHeight;
      if (rmHeight === 0 && clHeight === 0) {
        setScrollPage(1);
      } else {
        if (rmHeight < clHeight + 5) {
          setScrollPage(scrollPage + 1);
          loadOption(scrollPage + 1);
        }
      }
    }
  };

  const loadOption = (pageIndex: number): void => {
    const newPageSize = pageSize * pageIndex;
    const newOptionsData: SelectOption[] = [];
    const optionsLength = options.length > newPageSize ? newPageSize : options.length;
    if (keyWords != null) {
      const matches = getMatches(keyWords, options);
      matches.forEach((option, index) => {
        if (index < optionsLength) {
          newOptionsData.push(option);
        }
      });
    } else {
      options.forEach((option, index) => {
        if (index < optionsLength) {
          newOptionsData.push(option);
        }
      });
    }
    setOptionData(newOptionsData);
  };

  // 默认选择全部
  useEffect(() => {
    /**
     * 默认选中全部
     */
    if (defaultValue === SELECT_ALL) {
      setSelected(getAllValue(options));
      return;
    }

    if (optionSelected.length > 0) {
      // 全选值时
      if (selectAll && optionSelected?.length === options.length) {
        setSelected(['all', ...optionSelected]);
      } else {
        setSelected(optionSelected);
      }
    }

    if (clearAll) {
      setSelected([]);
    }
  }, [optionsMemo, defaultValue, mode, clearAll]);

  useEffect(() => {
    if (optionSelected?.length === options.length) {
      setSelected(['all', ...optionSelected]);
    } else {
      setSelected(optionSelected);
    }
  }, [value]);

  useEffect(() => {
    const defaultOption = options.filter(item => optionSelected.includes(item.value));
    const currentOptions = options.slice(0, pageSize).concat(defaultOption) as ExtendSelectOption[];
    const uniqOption = uniqOptionByValue(currentOptions, 'value');
    setOptionData(uniqOption);
  }, [optionSelected, optionsMemo]);

  function onSelectionchange(value: string[]): void {
    onChange(value.filter(value => value !== 'all'));
  }

  return (
    <ASelect
      className={join(styles.select, props.className)}
      disabled={disabled}
      loading={loading}
      value={optionSelected.length === 0 ? [] : selected}
      popupMatchSelectWidth={false}
      mode={mode}
      maxTagCount={maxTagCount}
      showSearch
      autoClearSearchValue={autoClearSearchValue}
      allowClear={allowClear}
      placeholder={placeholder}
      filterOption={filterOption}
      defaultValue={defaultValue}
      onSelect={(value): void => {
        onSearch('');
        if (value !== SELECT_ALL) {
          const newSelected = [...selected, value];
          if (newSelected?.length === options.length) {
            setSelected(['all', ...newSelected]);
          } else {
            setSelected(newSelected);
          }
          onSelectionchange([...selected, value]);
          return;
        }
        const selectedValue = getAllValue(keyWords !== '' ? getMatches(keyWords, options) : options);
        // 选择“全选”时，选择全部
        setSelected(selectedValue);
        onSelectionchange(selectedValue);
      }}
      onDeselect={(value): void => {
        // 取消某一项时，取消“全部”和这一项
        if (value !== SELECT_ALL) {
          setSelected(getDeselect(selected, value));
          onSelectionchange(getDeselect(selected, value));
          return;
        }
        // 取消“全选”时，取消全部
        setSelected([]);
        onSelectionchange([]);
        // 取消全选，展示默认option
        onSearch('');
      }}
      onClear={(): void => {
        setSelected([]);
        onSelectionchange([]);
      }}
      labelRender={labelRender}
      onPopupScroll={value => onScroll(value)}
      onSearch={onSearch}
      onBlur={onBlur}
      dropdownStyle={md === false ? { maxWidth: 300 } : { maxWidth: 600 }}
      dropdownRender={props.dropdownRender}
    >
      {getOptions(optionData, mode === 'multiple')}
    </ASelect>
  );
}

/**
 * 获取选项
 * @params options 基础选项
 * @params showAll 是否显示“全部”
 */
function getOptions(options: SelectOption[], showAll = false): JSX.Element[] {
  const extraOption = <ASelect.Option key='all' value={SELECT_ALL}>{t('全选')}</ASelect.Option>;
  const baseOptions = options.map((option, idx) => {
    const { label, value, tag } = option;

    return (
      <ASelect.Option key={idx} value={value} label={label} title={label}>
        <div className={styles.option}>
          <div className={styles.tag}>{tag != null && <Tag>{tag}</Tag>}</div>
          <div className={styles.label}>{label}</div>
        </div>
      </ASelect.Option >
    );
  });

  if (showAll && baseOptions.length > 0) {
    baseOptions.unshift(extraOption);
  }

  return baseOptions;
}

/**
 * 搜索过滤
 */
function filterOption(input: string, option?: SelectOption): boolean {
  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) || option?.value === 'all';
}

/**
 * 获取下啦列表中匹配项
 */
function getMatches(val: string, options: SelectOption[]): SelectOption[] {
  return [...options.filter(option => filterOption(val, option))];
}

/**
 * 选择“全部”时，获取全部数据
 */
function getAllValue(optionData: SelectOption[]): string[] {
  return [SELECT_ALL, ...optionData.map(op => op.value)];
}

/**
 * 反选
 */
function getDeselect(selected: string[], value: string): string[] {
  return selected.filter(itm => ![SELECT_ALL, value].includes(itm));
}

/**
 * 替代lodash的uniqBy
 * 使用 Array.from Map 保留插入顺序
 * @param arr
 * @param key
 * @returns
 */
function uniqOptionByValue(arr: ExtendSelectOption[], key: string): SelectOption[] {
  return Array.from(arr.reduce<Map<string, ExtendSelectOption>>((acc, cur: ExtendSelectOption) => {
    acc.set(cur[key] as string, cur);
    return acc;
  }, new Map()).values());
}
